/**
 * Authentication Gate Component
 *
 * Wraps application content to enforce authentication.
 * Automatically redirects unauthenticated users to the identity provider's login page.
 *
 * Features:
 * - Displays loading state while authentication status is being checked
 * - Auto-redirects unauthenticated users to login
 * - Passes through children when authenticated
 * - Logs authentication redirect events
 */

import React from 'react';
import { useAuth } from '../auth/hooks';
import { logAuthEvent, getStoredCorrelationId, generateCorrelationId } from '../auth/logging';
import { getAuthProvider } from '../auth/providers/oidc';
import { Stack, useTheme } from '@fti/design-system';

interface AuthGateProps {
  children: React.ReactNode;
}

/**
 * Loading indicator displayed while checking authentication status
 */
const LoadingIndicator: React.FC = () => {
  const { theme } = useTheme();

  // Theme-aware colors from design system
  const bgColor = theme.colors.background.default;
  const textColor = theme.colors.text.primary;
  const mutedColor = theme.colors.text.secondary;

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: bgColor,
        color: textColor
      }}
    >
      <Stack align="center" gap="sm" style={{ padding: '2rem', textAlign: 'center' }}>
        <div style={{ fontSize: '1.1rem', color: textColor }}>
          Checking authentication...
        </div>
        <div style={{ fontSize: '0.9rem', color: mutedColor }}>
          Please wait
        </div>
      </Stack>
    </div>
  );
};

/**
 * Authentication Gate
 *
 * Enforces authentication by:
 * 1. Showing loading state while checking auth status
 * 2. Auto-redirecting to login if not authenticated
 * 3. Rendering children if authenticated
 *
 * @param props.children - Content to render when authenticated
 */
export const AuthGate: React.FC<AuthGateProps> = ({ children }) => {
  const auth = useAuth();
  const [redirectInitiated, setRedirectInitiated] = React.useState(false);

  // After sign-out, the URL has ?signed_out — show the login page, don't auto-redirect
  const isPostLogout = window.location.search.includes('signed_out');

  React.useEffect(() => {
    // Only redirect if:
    // 1. Auth check is complete (not loading)
    // 2. User is not authenticated
    // 3. There's no auth error (don't redirect on errors - let user see the error)
    // 4. We haven't already initiated a redirect
    // 5. We're not returning from a sign-out (let user see the sign-in page)
    if (!auth.isLoading && !auth.isAuthenticated && !auth.error && !redirectInitiated && !isPostLogout) {
      const correlationId = getStoredCorrelationId() || generateCorrelationId();
      const provider = getAuthProvider();

      // Log redirect initiation
      logAuthEvent('login_redirect', 'Auto-redirecting unauthenticated user to login', {
        correlationId,
        provider
      });

      setRedirectInitiated(true);
      auth.signinRedirect();
    }
  }, [auth.isLoading, auth.isAuthenticated, auth.error, redirectInitiated, isPostLogout, auth]);

  // Show loading indicator while auth check is in progress
  if (auth.isLoading) {
    return <LoadingIndicator />;
  }

  // After sign-out, render children so App.tsx can show the sign-in page
  if (!auth.isAuthenticated && isPostLogout) {
    return <>{children}</>;
  }

  // Show nothing while redirecting (prevents flash of content)
  if (!auth.isAuthenticated && !auth.error) {
    return <LoadingIndicator />;
  }

  // If there's an error, let the children handle it (App.tsx shows error state)
  // If authenticated, render children
  return <>{children}</>;
};
